import React, { useRef, useState } from 'react';

import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Grid } from '@material-ui/core';
import Helmet from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router-dom';

import { VALIDATORS_MAP } from 'components/FormComponents/const';
import config from 'config/Config';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { authModel, passwordModel } from 'models';
import { CATEGORY_NAMES, METRIC_EVENT_NAMES } from 'metrics/constants';
import { useMatomo } from 'contexts/MatomoContext';
import {
  BuilderAuthCard,
  BuilderTextValidator,
  BuilderStyledButton,
  BuilderTitle,
  BuilderSubtitle,
  Box,
} from 'pages/auth/style';
import { ButtonComponent } from 'components';
import { COLORS } from 'theme/constants';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';

import { BuilderAuthMessage } from '../BuilderAuthMessage/BuilderAuthMessage';
import { BuilderContactUsFooter } from '../BuilderContactUsFooter';

function BuilderResetPassword() {
  const captchaRef = useRef<HCaptcha>(null);
  const { trackEvent } = useMatomo();
  const [state, setState] = useState({
    clientName: '',
    email: '',
  });
  const [validFields, setValidFields] = useState({
    clientName: true,
    email: true,
  });
  const [emailSent, setEmailSent] = useState(false);

  const history = useHistory();
  const { t } = useTranslation();
  const { handleServerError } = useServerErrorActions();

  const handlePost = () => {
    captchaRef?.current?.execute();
  };

  const onCaptchaVerify = (token: string) => {
    passwordModel
      .createPassword({ ...state, hCaptchaResponse: token })
      .then(() => {
        trackEvent({
          category: CATEGORY_NAMES.PUBTOOL_EVENTS,
          action: 'click',
          name: METRIC_EVENT_NAMES.RESET_PASSWORD,
        });
        setEmailSent(true);
      })
      .catch(error => handleServerError(error));
  };

  const handleChange = (item: Record<string, string>) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const onBackToSignIn = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_IN });
    redirectToSignIn();
  };

  const disableFormButton =
    Object.values(state).some(value => !value) || Object.values(validFields).some(value => !value);

  if (emailSent) {
    return (
      <BuilderAuthMessage
        tabTitle={t('pages.auth.ResetPassword.tabTitle')}
        title={t('pages.auth.ResetPassword.tabTitle')}
        text={
          <Trans
            i18nKey="pages.auth.ResetPassword.builderEmailSentText"
            components={{ bold: <b /> }}
            values={{ email: state.email }}
          />
        }
        onBack={onBackToSignIn}
      />
    );
  }

  return (
    <BuilderAuthCard>
      <Helmet title={t('pages.auth.ResetPassword.tabTitle')} />
      <Grid container direction="column" alignItems="center" spacing={6}>
        <Grid item xl={7} style={{ flex: 1 }}>
          <Box mb={6}>
            <ButtonComponent
              color="primary"
              variant="text"
              className="no-padding"
              text="< Back"
              onClick={onBackToSignIn}
              style={{ color: COLORS.builderBrandColor }}
            />
          </Box>
          <BuilderTitle>Reset Password</BuilderTitle>
          <Box mt={4} mb={8}>
            <BuilderSubtitle>{t('pages.auth.ResetPassword.subtext')}</BuilderSubtitle>
          </Box>
        </Grid>
        <Grid item xl={7} style={{ flex: 1 }}>
          <ValidatorForm onSubmit={handlePost}>
            <Box mb={3}>
              <BuilderTextValidator
                validators={[VALIDATORS_MAP.lowercaseLettersNumbersUnderscoreDashes]}
                label="World Name"
                value={state.clientName}
                fieldName={authModel.fields.space}
                handleChange={handleChange}
                autoComplete="on"
                onValidationChange={(isValid: boolean) =>
                  setValidFields(prevState => ({ ...prevState, clientName: isValid }))
                }
                disableMinHeight
              />
            </Box>
            <BuilderTextValidator
              validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.isEmail]}
              label={t('common.forms.labels.email')}
              value={state.email}
              fieldName={authModel.fields.email}
              type="email"
              handleChange={handleChange}
              autoComplete="email"
              onValidationChange={(isValid: boolean) => setValidFields(prevState => ({ ...prevState, email: isValid }))}
              disableMinHeight
            />
            <Box mt={5}>
              <BuilderStyledButton
                fullWidth
                color="primary"
                type="submit"
                text={t('pages.auth.ResetPassword.submit')}
                disabled={disableFormButton}
              />
              <HCaptcha
                onVerify={onCaptchaVerify}
                sitekey={config.captcha_key ?? ''}
                ref={captchaRef}
                size="invisible"
              />
            </Box>
          </ValidatorForm>
        </Grid>
      </Grid>
      <BuilderContactUsFooter />
    </BuilderAuthCard>
  );
}

export default BuilderResetPassword;
